import styled, { css } from 'styled-components';
import { flexGap } from '../../../utils/flexGap';

interface WaitingRoomContentProps {
	$offsetTop?: number;
	$offsetBottom?: number;
}

export const WaitingRoomContainer = styled.div`
	${({ theme }) => css`
		width: 100%;
		height: auto;
		background-image: url(${theme.backgroundImage});
		background-size: cover;
		position: relative;

		@media ${theme.screens.screenM} {
			height: 100vh;
			height: calc(var(--vh, 1vh) * 100);
		}
	`};
`;

export const WaitingRoomContent = styled.div<WaitingRoomContentProps>`
	${({ theme, $offsetTop = 0, $offsetBottom = 0 }) => css`
		width: 100%;
		display: flex;
		flex-direction: column;

		padding-left: 8px;
		padding-right: 8px;
		padding-top: ${$offsetTop + 40}px;
		padding-bottom: ${$offsetBottom + 20}px;

		@media ${theme.screens.screenS} {
			overflow-y: scroll;
			--webkit-overflow-scrolling: touch;
		}

		@media ${theme.screens.screenGTS} {
			padding-left: 76px;
			padding-right: 76px;

			padding-top: ${$offsetTop + 68}px;
			padding-bottom: 0;

			height: 100vh;
			height: calc(var(--vh, 1vh) * 100);
			
			justify-content: center;
			align-items: center;
		}
	`};
`;

export const WaitingRoomSetup = styled.div`
	${({ theme }) => css`
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: end;
		${flexGap(`${27}px`, false)}
		padding: 0 15px 26px 15px;

		@media ${theme.screens.screenGTS} {
			justify-content: center;
			padding-bottom: 0;
		}
	`};
`;

export const WaitingRoomPreview = styled.div`
	${({ theme }) => css`
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		${flexGap(`${27}px`, false)}

		@media ${theme.screens.screenGTS} {
			flex-grow: 1;
			justify-content: center;
			padding-bottom: 0;
		}
	`};
`;

export const WaitingRoomMeetInfo = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
`;

export const WaitingRoomTitle = styled.div`
	${({ theme }) => css`
		color: ${theme.colors.brand.primary[300]};
		font-size: ${theme.fontSizes.default};
		font-weight: ${theme.fontWeights.bold};
		text-transform: uppercase;
		text-align: center;

		max-width: 194px;

		@media ${theme.screens.screenGTS} {
			font-size: ${theme.fontSizes.l};
			max-width: 258px;
		}
	`};
`;

export const SaveMeetConfig = styled.div`
	color: #ffffff;
`;

export const PermissionsMessage = styled.div`
	${({ theme }) => css`
		color: ${theme.colors.neutral[100]};
		font-size: ${theme.fontSizes.default};
		text-align: center;
		max-width: 688px;
	`};
`;

export interface WaitingRoomLogoProps {
	$top: number;
}

export const WaitingRoomLogo = styled.div<WaitingRoomLogoProps>`
	${({ theme, $top }) => css`
		position: absolute;
		top: ${$top}px;
		width: 132px;

		left: 50%;
		transform: translateX(-50%);

		@media ${theme.screens.screenGTS} {
			width: 264px;
		}

		@media ${theme.screens.screenL} {
			width: 360px;
		}
	`};
`;

export const Logo = styled.img`
	opacity: 1;
	width: 100%;
`;

export const Checkbox = styled('input')`
	${({ theme }) => css`
		accent-color: ${theme.colors.brand.primary[300]};
	`};
`;

export const WaitingRoomCard = styled.div`
	${({ theme }) => css`
		width: 100%;
		border-radius: ${theme.radii.m};
		overflow: hidden;

		@media ${theme.screens.screenL} {
			overflow: initial;
			max-width: 960px;
		}
	`};
`;

export const WaitingRoomCardHeader = styled.div`
	${({ theme }) => css`
		position: relative;
		width: 100%;
		background-color: ${theme.colors.brand.accent[300]};
		font-size: ${theme.fontSizes.sm};
		color: ${theme.colors.neutral[100]};
		text-align: center;

		padding: 14px 38px;
		line-height: 1.22;

		display: grid;
		place-content: center;
		text-transform: uppercase;
		
		border-top-left-radius: ${theme.radii.m};
		border-top-right-radius: ${theme.radii.m};

		@media ${theme.screens.screenGTS} {
			font-size: ${theme.fontSizes.xxl};
		}

		@media ${theme.screens.screenL} {
			max-width: 960px;
			place-content: start;
			text-align: start;
			padding: 24px 64px;
		}
	`};
`;

export const NextVideoConsultationTitle = styled.div`
	${({ theme }) => css`
		font-size: ${theme.fontSizes.l};
		font-weight: ${theme.fontWeights.medium};

		@media ${theme.screens.screenGTS} {
			font-size: ${theme.fontSizes.xxxl};
		}
	`};
`;

export const NextVideoConsultationSubtitle = styled.div`
	${({ theme }) => css`
		background-color: ${theme.colors.brand.accent[300]};
	`};
`;

export const NextVideoConsultationImage = styled.img`
	position: absolute;
	bottom: 0;
	right: 64px;
	height: 80%;
`;

export const WaitingRoomCardContent = styled.div`
	${({ theme }) => css`
		background-color: ${theme.colors.neutral[100]};
		padding: 16px;
		display: grid;
		grid-template-columns: repeat(1, 1fr);
		grid-template-rows: repeat(1, 1fr);
		justify-items: center;
		gap: 30px;
		align-items: center;

		border-bottom-left-radius: ${theme.radii.m};
		border-bottom-right-radius: ${theme.radii.m};

		@media ${theme.screens.screenGTS} {
			padding: 36px;
		}

		@media ${theme.screens.screenL} {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			grid-template-rows: repeat(1, 1fr);
			justify-items: start;
			padding: 48px 76px;
		}
	`};
`;

export const WaitingRoomBottom = styled.div`
	${({ theme }) => css`
		position: fixed;
		width: 100%;
		bottom: 0;
		background-color: ${theme.colors.neutral[200]};

		padding: 14px 44px;
	`};
`;

export const WaitingRoomMeetControlsContainer = styled.div`
	position: relative;
`;
